import { col, POTENTIAL_RATES, RESERVOIR_PRESSURE, type Shape } from './base.ts';

/**
 * Long-term inactivity + monthly classification on the base `Inactive_String`
 * view (richer than the upstream `InactiveString_Agent`, which exposes only
 * status/category/downtime-start). Bare source columns — `suffix ===
 * canonical`, `prefix:''`. Stored status codes are title-cased; the
 * stakeholder-documented value sets below are pending verification against
 * live data, so they are descriptive hints, not declared enums.
 */
export const INACTIVE_STATUS: Shape = [
  col(
    'stringStatus',
    'unitless',
    "Binary activity rollup: 'Active' or 'Inactive' — the gate for downstream rate work.",
  ),
  col(
    'stringStatusAll',
    'unitless',
    "Most-specific monthly classification tier. Values include 'Abandoned', 'Suspended', 'Approved P&A', 'Standby', 'New Drilled', 'Existing', 'Healthy', 'Problematic', 'Inactive'. Rollups: Healthy/Problematic → Active; New Drilled/Existing → Project; every tier except Abandoned counts as Operational.",
  ),
  col(
    'categoryCode',
    'unitless',
    'Root-cause category of the inactivity — the top level of the inactive drill-down (category → sub-category → problem name).',
  ),
  col(
    'inactiveCategory',
    'unitless',
    "Root-cause sub-category of the inactivity. Sub-categories by category — Integrity Related: 'Wellhead Integrity', 'Well Integrity Sub-surface', 'Sub-surface Integrity Low Risk', 'Facility Integrity'; Tie-in Delay: 'Newly Drilled Tie-in Delay', 'Newly Drilled Un-comm', 'Worked Over Tie-in Delay', 'Worked Over Un-comm'; Flow Assurance: 'Surface Flow Assurance', 'Sub-surface Flow Assurance'; Reservoir Related: 'Reservoir Management', 'Under Investigation', 'Future Development'; Dynamic Strings: 'Drilling Handover', 'SIMOPS', 'Facility HSE', 'Facilities Shutdown', 'Facilities Limitation'.",
  ),
  col(
    'problemName',
    'unitless',
    'Named problem behind the inactivity — the leaf of the inactive drill-down; display it alongside the category in inactive breakdowns.',
  ),
  col('actionStatus', 'unitless', "Status of the remediation action for the inactivity (e.g. 'SURFACE WORK ACTION')."),
  col('actionDate', 'unitless', 'Date of the remediation action.', { valueType: 'date' }),
  col('downtimeStartDate', 'unitless', 'Date the string went offline.', { valueType: 'date' }),
  col('duration', 'durationDays', 'Downtime duration.'),
  col(
    'expectedRate',
    'unitless',
    'Expected production rate once restored. Never a lost-production baseline — that is the last positive allocated rate.',
  ),
];

/**
 * Gas-injection performance on `InjectionPerformance_String_Agent` (B2):
 * rates, targets, technical rates, well-head/bottom-hole pressures, and
 * cumulative/downhole gas-injection volumes. Bare source columns —
 * `suffix === canonical`, `prefix:''`.
 */
export const GAS_INJECTION_PERFORMANCE: Shape = [
  col('giRate', 'gasRate', 'Current gas-injection rate.'),
  col('giTargetRate', 'gasRate', 'Target gas-injection rate.'),
  col('giTechnicalRate', 'gasRate', 'Technical (maximum sustainable) gas-injection rate.'),
  col('giWHP', 'pressureAbsolute', 'Gas-injection well-head pressure.'),
  col('giBHP', 'pressureAbsolute', 'Gas-injection bottom-hole pressure.'),
  col('cumulativeGasInjectionVolume', 'gasVolume', 'Cumulative gas-injection volume.', {
    suffix: 'cumulativeGasInjVol',
  }),
  col('cumulativeGasInjectionPressure', 'pressureAbsolute', 'Cumulative gas-injection pressure.', {
    suffix: 'cumulativeGasInjPress',
  }),
  col('downholeGasInjectionVolume', 'gasVolume', 'Downhole gas-injection volume.'),
];

/**
 * Water-injection performance on `InjectionPerformance_String_Agent` (B2):
 * rates, targets, technical rates, well-head/bottom-hole pressures, and
 * cumulative/downhole water-injection volumes. Bare source columns —
 * `suffix === canonical`, `prefix:''`.
 */
export const WATER_INJECTION_PERFORMANCE: Shape = [
  col('wiRate', 'liquidRate', 'Current water-injection rate.'),
  col('wiTargetRate', 'liquidRate', 'Target water-injection rate.'),
  col('wiTechnicalRate', 'liquidRate', 'Technical (maximum sustainable) water-injection rate.'),
  col('wiWHP', 'pressureAbsolute', 'Water-injection well-head pressure.'),
  col('wiBHP', 'pressureAbsolute', 'Water-injection bottom-hole pressure.'),
  col('cumulativeWaterInjectionVolume', 'liquidVolume', 'Cumulative water-injection volume.', {
    suffix: 'cumulativeWaterInjVol',
  }),
  col('cumulativeWaterInjectionPressure', 'pressureAbsolute', 'Cumulative water-injection pressure.', {
    suffix: 'cumulativeWaterInjPress',
  }),
  col('downholeWaterInjectionVolume', 'liquidVolume', 'Downhole water-injection volume.'),
];

/**
 * Injection diagnostics on `InjectionPerformance_String_Agent` (B2):
 * efficiency, efficiency rank/flag, Hall-plot line/interpretation, and
 * injectivity index. Bare source columns — `suffix === canonical`, `prefix:''`.
 */
export const INJECTION_DIAGNOSTICS: Shape = [
  col('injectionEfficiency', 'fraction', 'Injection efficiency.'),
  col('injectionEfficiencyRank', 'unitless', 'Injection-efficiency rank.'),
  col('injectionEfficiencyFlag', 'unitless', 'Injection-efficiency flag.'),
  col('hallLine', 'unitless', 'Hall-plot line value.'),
  col('hallInterpretation', 'unitless', 'Hall-plot interpretation.'),
  col('injectivityIndex', 'productivityIndex', 'Injectivity index.'),
];

/**
 * Unconstrained well potential on `WellPotential_String_Agent` (B2): potential
 * oil/gas/water production and water/gas injection. Bare source columns —
 * `suffix === canonical`, `prefix:''`. Distinct from `PHASE_RATES` (different
 * `potential*` member names, plus the injection potentials).
 */
export const WELL_POTENTIAL: Shape = [...POTENTIAL_RATES];

/**
 * Well online state on `WellStatus_String_Agent` (B2): online status/reason,
 * steady-state and slugging indicators, hours online. Bare source columns —
 * `suffix === canonical`, `prefix:''`.
 */
export const WELL_ONLINE_STATE: Shape = [
  col('onlineStatus', 'unitless', 'Online state (numeric).'),
  col('onlineStatusReason', 'unitless', 'Reason for the current online state.'),
  col('steadyStateStatus', 'unitless', 'Flow-stability (steady-state) status.'),
  col('sluggingStatus', 'unitless', 'Slugging indicator.'),
  col('hoursOnline', 'unitless', 'Hours online.'),
];

/**
 * Well classification on `WellStatus_String_Agent` (B2): lift type, well type,
 * product, WAG flag, and instrumentation flag. Bare source columns —
 * `suffix === canonical`, `prefix:''`.
 */
export const WELL_CLASSIFICATION: Shape = [
  col('liftType', 'unitless', 'Artificial-lift method.'),
  col('wellType', 'unitless', 'Well classification.'),
  col('product', 'unitless', 'Primary product type.'),
  col('wag', 'unitless', 'Water-alternating-gas injection flag.'),
  col('instrumented', 'unitless', 'Downhole-instrumentation flag (numeric).'),
];

/**
 * Valve close-states on `WellStatus_String_Agent` (B2): surface, sub-surface,
 * and wing safety-valve close states. Bare source columns —
 * `suffix === canonical`, `prefix:''`.
 */
export const WELL_VALVE_STATES: Shape = [
  col('surfaceSafetyValveCloseState', 'unitless', 'Surface safety-valve close state (numeric).'),
  col('subSurfaceSafetyValveCloseState', 'unitless', 'Sub-surface safety-valve close state (numeric).'),
  col('wingValveCloseState', 'unitless', 'Wing-valve close state (numeric).'),
];

/**
 * Pressure compliance + reservoir-pressure estimates on
 * `WellStatus_String_Agent` (B2): well-test validation, pressure
 * compliance/source, and reservoir pressure at datum / mid-perforation. Bare
 * source columns — `suffix === canonical`, `prefix:''`. Distinct from
 * `WELL_PRESSURES` (these are reservoir-pressure estimates + compliance, not
 * flowing well-bore pressures).
 */
export const WELL_PRESSURE_COMPLIANCE: Shape = [
  col('wellTestValidation', 'unitless', 'Well-test validation status.'),
  col('pressureCompliance', 'unitless', 'Pressure-compliance status.'),
  ...RESERVOIR_PRESSURE,
];

/**
 * Injection pressure/rate guidelines on `InjPressGuideline_String_Agent` (B2):
 * the six gas/water-injection max well-head (WHIP) / bottom-hole (BHIP)
 * injection pressures and max injection rates. Bare source columns —
 * `suffix === canonical`, `prefix:''`. (BHIP/WHIP = bottom-hole / well-head
 * injection pressure — preserved verbatim.)
 */
export const INJ_PRESS_GUIDELINE: Shape = [
  col('giMaxWHIP', 'pressureAbsolute', 'Maximum gas-injection well-head pressure.'),
  col('giMaxBHIP', 'pressureAbsolute', 'Maximum gas-injection bottom-hole pressure.'),
  col('giMaxInjectionRate', 'gasRate', 'Maximum gas-injection rate.', { suffix: 'giMaxInjRate' }),
  col('wiMaxWHIP', 'pressureAbsolute', 'Maximum water-injection well-head pressure.'),
  col('wiMaxBHIP', 'pressureAbsolute', 'Maximum water-injection bottom-hole pressure.'),
  col('wiMaxInjectionRate', 'liquidRate', 'Maximum water-injection rate.', { suffix: 'wiMaxInjRate' }),
];

/**
 * Producer operating guidelines on `ProducerGuidelines_String_Agent` (B2):
 * max GOR, min BHP/WHP, max liquid/oil rate. Bare source columns —
 * `suffix === canonical`, `prefix:''`.
 */
export const PRODUCER_GUIDELINES: Shape = [
  col('maxGOR', 'gasOilRatio', 'Maximum allowable gas-oil ratio.'),
  col('minBHP', 'pressureAbsolute', 'Minimum allowable bottom-hole pressure.'),
  col('maxLiquidRate', 'liquidRate', 'Maximum allowable liquid rate.'),
  col('maxOilRate', 'liquidRate', 'Maximum allowable oil rate.'),
  col('minWHP', 'pressureAbsolute', 'Minimum allowable well-head pressure.'),
];

/**
 * Production-plan allowable on `Prodplan_String_Agent` (B2): quota
 * contribution, total-gas WSOPR, and technical oil rate (`opWpr`). Bare source
 * columns — `suffix === canonical`, `prefix:''`. (`quotaContr` = quota
 * contribution; `wsoprTotalGas` / `opWpr` are model binding names — preserved
 * verbatim.)
 */
export const PRODPLAN_ALLOWABLE: Shape = [
  col('quotaContribution', 'liquidRate', 'Oil-quota contribution.', { suffix: 'quotaContr' }),
  col('wsoprTotalGas', 'gasRate', 'Allowable total-gas rate (WSOPR gas).'),
  col('opWpr', 'liquidRate', 'Technical oil-production rate (operating WPR).'),
];

/**
 * Injection allowable on `Prodplan_String_Agent` (B2): water/gas allowable
 * rates and contributions plus technical injection rates. Bare source columns —
 * `suffix === canonical`, `prefix:''`. NOTE: `wiTechnicalRate` /
 * `giTechnicalRate` share canonical names with the InjectionPerformance shapes
 * but here describe the allowable PLAN (different source/grain semantic) — kept
 * separate by design, never cross-wired.
 */
export const INJECTION_ALLOWABLE: Shape = [
  col('wiAllowableRate', 'liquidRate', 'Allowable water-injection rate.'),
  col('giAllowableRate', 'gasRate', 'Allowable gas-injection rate.'),
  col('wiAllowableContribution', 'liquidVolume', 'Water-injection allowable contribution.', {
    suffix: 'wiAllowableContr',
  }),
  col('giAllowableContribution', 'gasVolumeModel', 'Gas-injection allowable contribution.', {
    suffix: 'giAllowableContr',
  }),
  col('wiTechnicalRate', 'liquidRate', 'Technical water-injection rate (plan limit).'),
  col('giTechnicalRate', 'gasRate', 'Technical gas-injection rate (plan limit).'),
];

/**
 * Well-movement record metadata on `WellMovement_Agent` (B2): scenario,
 * description, source, status fields, ticket id, and denormalized well
 * name/type. All SCALAR. Bare source columns — `suffix === canonical`,
 * `prefix:''`. (`wellName`/`wellType` are denormalized scalar strings on the
 * record, distinct from the `wellString` relation carried by `keep`.)
 */
